/**
 * 🔍 ChunkDebugModal - 청크 처리 디버그 팝업
 * 
 * 첫 번째 청크(Reference)와 나머지 청크 요약을 보여줍니다.
 * 사용자가 청크 간 컨텍스트 전달이 제대로 동작하는지 확인할 수 있습니다.
 */
import React from 'react';
import { X, CheckCircle, XCircle, Clock, FileText, Layers } from 'lucide-react';

const ChunkDebugModal = ({ isOpen, onClose, chunkDetails, referenceExample, totalPages }) => {
    if (!isOpen || !chunkDetails) return null;

    const firstChunk = chunkDetails[0];
    const remainingChunks = chunkDetails.slice(1);
    const totalChunks = chunkDetails.length;
    const successCount = chunkDetails.filter(c => c.success).length;
    const totalTime = chunkDetails.reduce((sum, c) => sum + (c.timing?.total || 0), 0);

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-2xl w-[700px] max-h-[85vh] overflow-hidden"
                onClick={e => e.stopPropagation()}
            >
                {/* 헤더 */}
                <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                    <div className="flex items-center gap-3">
                        <Layers size={22} />
                        <div>
                            <h2 className="font-bold text-lg">청크 처리 디버그</h2>
                            <p className="text-xs text-indigo-100">
                                {totalChunks}개 청크 | {totalPages}페이지 | {(totalTime / 1000).toFixed(1)}초
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* 바디 */}
                <div className="p-5 overflow-y-auto max-h-[calc(85vh-140px)]">
                    {/* 첫 번째 청크 (Reference) */}
                    <div className="mb-5">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                                📌 Reference Chunk
                            </span>
                            <span className="text-sm text-gray-600">
                                (페이지 {firstChunk?.pages})
                            </span>
                            {firstChunk?.success ? (
                                <CheckCircle size={16} className="text-green-500" />
                            ) : (
                                <XCircle size={16} className="text-red-500" />
                            )}
                        </div>

                        {/* Reference Example JSON */}
                        {referenceExample && (
                            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                <div className="flex items-center gap-2 mb-2">
                                    <FileText size={14} className="text-gray-400" />
                                    <span className="text-xs text-gray-400 font-medium">
                                        이후 청크에 전달된 예시 형식
                                    </span>
                                </div>
                                <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                                    {JSON.stringify(referenceExample, null, 2)}
                                </pre>
                            </div>
                        )}

                        {/* 첫 번째 청크 통계 */}
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                                <Clock size={12} />
                                {firstChunk?.timing?.total || 0}ms
                            </span>
                            <span>pageResults: {firstChunk?.pageResultsCount || 0}</span>
                            <span>items: {firstChunk?.itemsCount || 0}</span>
                        </div>
                    </div>

                    {/* 구분선 */}
                    {remainingChunks.length > 0 && (
                        <div className="border-t border-gray-200 my-4" />
                    )}

                    {/* 나머지 청크 요약 (N번째로 1줄) */}
                    {remainingChunks.length > 0 && (
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                                    🔁 나머지 {remainingChunks.length}개 청크
                                </span>
                                <span className="text-xs text-gray-500">
                                    [+Reference 적용됨]
                                </span>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-3">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-gray-500 border-b border-gray-200">
                                            <th className="py-2 text-left font-medium">청크</th>
                                            <th className="py-2 text-left font-medium">페이지</th>
                                            <th className="py-2 text-center font-medium">상태</th>
                                            <th className="py-2 text-right font-medium">시간</th>
                                            <th className="py-2 text-right font-medium">결과</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {remainingChunks.map((chunk, i) => (
                                            <tr key={i} className="border-b border-gray-100 last:border-0">
                                                <td className="py-2 font-medium text-gray-700">
                                                    #{chunk.chunkIdx + 1}
                                                </td>
                                                <td className="py-2 text-gray-600">{chunk.pages}</td>
                                                <td className="py-2 text-center">
                                                    {chunk.success ? (
                                                        <CheckCircle size={14} className="text-green-500 inline" />
                                                    ) : (
                                                        <XCircle size={14} className="text-red-500 inline" />
                                                    )}
                                                </td>
                                                <td className="py-2 text-right text-gray-500">
                                                    {chunk.timing?.total || 0}ms
                                                </td>
                                                <td className="py-2 text-right text-gray-600">
                                                    {chunk.pageResultsCount || chunk.itemsCount || 0}개
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* 푸터 */}
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <p className="text-xs text-gray-500">
                        💡 Reference Example이 이후 청크에 전달되어 일관된 형식으로 추출됩니다.
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-800 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChunkDebugModal;
